import type { UpcomingMeetingState } from './meeting-row-state';
import { meetingIsOver, meetingView, phase1State, type MeetingRowFields } from './meeting-view';

/**
 * What an upcoming meeting's "Join this meeting" chip does, by the meeting's time and her live state.
 *
 * Before the meeting the chip sends her (the Meetings list's own Join request); from a minute before the
 * start it opens the meeting link, and sends her too if she is not coming. A status line in the Meetings
 * list's words sits under it throughout. A chip that would do nothing is not drawn.
 *
 * Import-free for the same reason as `meeting-row-state.ts`: a jest test must not reach react-query.
 */

/** common-lib `meetingJoinOptionId`, spelled out so this module stays import-free. */
const MEETING_JOIN_OPTION_PREFIX = 'meeting-join:';

/**
 * The connect chip `JoinMeeting` offers an owner who has not connected Meetings. The contract with the Java
 * tool (ticket 11): change it only on both sides at once.
 */
const MEETING_CONNECT_OPTION_PREFIX = 'meeting-connect:';

/** The harness's `INLINE_JOIN_WINDOW_MS`: inside it a Join request joins her at once instead of arming. */
export const MEETING_JOIN_LIVE_LEAD_MS = 60 * 1000;

export interface MeetingJoinChipView {
  /** `send` asks her in and opens nothing; `open` opens the meeting link. */
  chip: 'send' | 'open' | null;
  requestsJoin: boolean;
  /** The old state of a row the status line is drawn for, or null for no line. The line itself is `meetingView`'s. */
  line: UpcomingMeetingState | null;
}

type MeetingTimes = { startsAt: string; endsAt?: string };

const NOTHING: MeetingJoinChipView = { chip: null, requestsJoin: false, line: null };

export function meetingJoinChipEventId(optionId: string): string | undefined {
  return eventIdAfter(MEETING_JOIN_OPTION_PREFIX, optionId);
}

function eventIdAfter(prefix: string, optionId: string): string | undefined {
  if (!optionId.startsWith(prefix)) return undefined;
  return optionId.slice(prefix.length) || undefined;
}

type ChipOption = { id: string; url?: string };

/** Which chip a row's options draw. Both meeting chips are the row's only option; anything else is plain choices. */
export type MeetingChoiceChip<O extends ChipOption> =
  | { kind: 'join'; eventId: string; option: O }
  | { kind: 'connect'; eventId: string; option: O }
  | { kind: 'choices' };

export function meetingChoiceChip<O extends ChipOption>(options: readonly O[]): MeetingChoiceChip<O> {
  if (options.length !== 1) return { kind: 'choices' };
  const option = options[0];
  // A connect never opens its link, so a url on it changes nothing.
  const connectEventId = eventIdAfter(MEETING_CONNECT_OPTION_PREFIX, option.id);
  if (connectEventId) return { kind: 'connect', eventId: connectEventId, option };
  const joinEventId = option.url ? eventIdAfter(MEETING_JOIN_OPTION_PREFIX, option.id) : undefined;
  if (joinEventId) return { kind: 'join', eventId: joinEventId, option };
  return { kind: 'choices' };
}

/**
 * The first step of a connect chip's tap, from a FRESH schedule read. A `POST connection` over an existing
 * connection updates it, so an owner who is already connected gets the join request only: a connect there
 * would reset "join automatically" to the first-connect default.
 */
export function meetingConnectStep(schedule: { connection: unknown; calendars: readonly unknown[] }): 'join' | 'calendar' | 'connect' {
  if (schedule.connection) return 'join';
  return schedule.calendars.length === 0 ? 'calendar' : 'connect';
}

/**
 * After the connect: whether the join chip's own request still has to go out for this meeting. `unavailable`
 * covers a meeting the week's schedule does not hold, one that ended, and states where a request arms nothing.
 */
export function meetingConnectJoin(meeting: (MeetingTimes & MeetingRowFields) | undefined, now: number): 'request' | 'done' | 'unavailable' {
  if (!meeting) return 'unavailable';
  const view = meetingJoinChipView(meeting, now);
  if (view.requestsJoin) return 'request';
  if (!view.line) return 'unavailable';
  const state = phase1State(meeting);
  if (state) return state === 'will_join' || state === 'in_meeting' ? 'done' : 'unavailable';
  return view.line === 'scheduled' || view.line === 'in_meeting' ? 'done' : 'unavailable';
}

function hasEnded(meeting: MeetingTimes, now: number): boolean {
  const endsAt = meeting.endsAt ? Date.parse(meeting.endsAt) : NaN;
  return Number.isFinite(endsAt) && endsAt <= now;
}

function isLive(meeting: MeetingTimes, now: number): boolean {
  return now >= Date.parse(meeting.startsAt) - MEETING_JOIN_LIVE_LEAD_MS;
}

export function meetingJoinChipView(meeting: MeetingTimes & MeetingRowFields, now: number): MeetingJoinChipView {
  if (hasEnded(meeting, now) || meetingIsOver(meeting)) return NOTHING;
  // The row's own action decides: Add (or a row without `meetingState` that is not joining or skipped). `paused`
  // is the site's server-side switch, where a request is recorded but arms nothing, and a declined meeting offers
  // no Add.
  const action = meetingView(meeting).action;
  const requestsJoin = action === 'add' || action === 'join' || action === 'undo';
  if (isLive(meeting, now)) return { chip: 'open', requestsJoin, line: meeting.state };
  return { chip: requestsJoin ? 'send' : null, requestsJoin, line: meeting.state };
}

/** When the view next changes on its own, so the chip can re-render then; undefined when it never will. */
export function nextMeetingJoinChange(meeting: MeetingTimes, now: number): number | undefined {
  if (hasEnded(meeting, now)) return undefined;
  if (!isLive(meeting, now)) return Date.parse(meeting.startsAt) - MEETING_JOIN_LIVE_LEAD_MS;
  const endsAt = meeting.endsAt ? Date.parse(meeting.endsAt) : NaN;
  return Number.isFinite(endsAt) ? endsAt : undefined;
}
